import React from "react";

const PREGNANCY_DASH_PATTERN = "6 4";

/**
 * Splits a series into consecutive runs of same-styled segments.
 *
 * A segment (the join between two adjacent points) is dashed when either endpoint was recorded
 * during a pregnancy. Weight rises normally through pregnancy, so a solid line across those
 * readings reads as a worsening trend — dashing marks the stretch as not comparable without
 * hiding the measurements, which are real.
 *
 * Runs are grouped rather than emitting one path per segment so adjacent same-styled segments
 * keep their line joins.
 *
 * @param {{x: number, y: number, isPregnant: boolean}[]} points
 * @returns {{dashed: boolean, points: object[]}[]}
 */
const splitIntoStyledRuns = points => {
    const runs = [];

    for (let index = 0; index < points.length - 1; index++) {
        const start = points[index];
        const end = points[index + 1];
        const dashed = start.isPregnant || end.isPregnant;
        const currentRun = runs[runs.length - 1];

        if (currentRun && currentRun.dashed === dashed) {
            currentRun.points.push(end);
        } else {
            runs.push({ dashed, points: [start, end] });
        }
    }

    return runs;
};

const LineComponent = ({ series, lineGenerator, xScale, yScale }) =>
    series.flatMap(({ id, data, color }) => {
        const points = data.map(d => ({
            x: xScale(d.data.x),
            y: yScale(d.data.y),
            isPregnant: Boolean(d.data.isPregnant)
        }));

        // A single point has no segments to style — keep the original single-path behaviour.
        if (points.length < 2) {
            return [<path key={id} fill="none" stroke={color} d={lineGenerator(points)} />];
        }

        return splitIntoStyledRuns(points).map((run, index) => (
            <path
                key={`${id}-${index}`}
                fill="none"
                stroke={color}
                strokeDasharray={run.dashed ? PREGNANCY_DASH_PATTERN : undefined}
                d={lineGenerator(run.points)}
            />
        ));
    });

export default LineComponent;
